import axios from 'axios';
import type { AuthResponse } from '../types/auth';
import { getRefreshToken } from '../util';
import {
  apiURL,
  keycloakBaseUrl,
  keycloakClientId,
  keycloakViewerClientId,
} from '../util/constants';

let refreshingToken: Promise<string> | null = null;
let refreshingKeycloakToken: Promise<string> | null = null;

async function fetchKeycloakAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken();
  const keycloakClientKey = localStorage.getItem('keycloak_login_client') ?? 'main';
  const clientId =
    keycloakClientKey === 'viewer' ? keycloakViewerClientId : keycloakClientId;

  if (!refreshToken || !clientId || !keycloakBaseUrl) {
    throw new Error('Missing Keycloak refresh context');
  }

  const refreshResponse = await axios.post<AuthResponse>(
    `${keycloakBaseUrl}/realms/master/protocol/openid-connect/token`,
    new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: clientId,
    }),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    },
  );

  const { access_token, refresh_token: newRefreshToken } = refreshResponse.data;

  if (access_token) {
    localStorage.setItem('access_token', access_token);
  }

  if (newRefreshToken) {
    localStorage.setItem('refresh_token', newRefreshToken);
  }

  return access_token ?? '';
}

async function fetchBackendAccessToken(): Promise<string> {
  const response = await axios.post<{
    accessToken?: string;
    access_token?: string;
  }>(`${apiURL}/auth/token/refresh`, {}, { withCredentials: true });

  return response.data.accessToken ?? response.data.access_token ?? '';
}

async function runKeycloakRefresh(): Promise<string> {
  try {
    const token = await fetchKeycloakAccessToken();
    refreshingKeycloakToken = null;
    return token;
  } catch (error) {
    refreshingKeycloakToken = null;
    throw error;
  }
}

async function runBackendRefresh(): Promise<string> {
  try {
    const token = await fetchBackendAccessToken();
    refreshingToken = null;
    return token;
  } catch (error) {
    refreshingToken = null;
    throw error;
  }
}

/** Pre–T&C: one Keycloak refresh at a time for parallel 401s. */
export const refreshKeycloakTokenSafely = (): Promise<string> => {
  if (refreshingKeycloakToken) {
    return refreshingKeycloakToken;
  }

  refreshingKeycloakToken = runKeycloakRefresh();
  return refreshingKeycloakToken;
};

/** Post–T&C: one backend refresh at a time for parallel 401s. */
export const refreshAccessTokenSafely = (): Promise<string> => {
  if (refreshingToken) {
    return refreshingToken;
  }

  refreshingToken = runBackendRefresh();
  return refreshingToken;
};
